/**
 * DocSpecPreview — renders a DocSpec (the structured report contract the
 * playbook agent composes via compose_document) as a "paper"-style inline
 * preview that mirrors the DOCX output.
 *
 * The DocSpec v2 schema lives in backend/services/document_renderer.py; this
 * component is a faithful, lightweight HTML rendering of the same vocabulary
 * (cover + heading / text / callout / metrics / table / list / quote / method
 * / source / divider / page_break). It mirrors the renderer's guardrails:
 * internal staging IDs are scrubbed, table source attributions are dropped, and
 * each metric/table can carry a "How this was calculated" note. Read-only —
 * callers pass a resolved `spec`.
 */
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DocSpec, DocSpecSection, DocSpecMetricItem } from '../../../types/playbooks';

interface DocSpecPreviewProps {
  spec: DocSpec;
  /** Constrain the preview height and make it scroll internally. */
  maxHeight?: number | string;
}

/**
 * Renders a markdown string. `inline` collapses the paragraph wrapper so the
 * text can sit inside list items, quotes, etc. without forcing a block.
 * Structural labels (Finding / Suggestion / So what) are broken onto new lines
 * to mirror the DOCX renderer.
 */
function Markdown({ text, sx, inline = false }: { text?: string; sx?: object; inline?: boolean }) {
  const value = insertLabelLineBreaks(String(text ?? ''));
  if (!value) return null;
  const lines = value.split('\n').filter((line) => line.length > 0);
  return (
    <Box
      component={inline ? 'span' : 'div'}
      sx={{
        '& p': inline ? { m: 0, display: 'inline' } : { my: 0.5 },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
        '& a': { color: 'inherit' },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.85em',
          bgcolor: 'rgba(0,0,0,0.06)',
          px: 0.5,
          borderRadius: 0.5,
        },
        '& ul, & ol': { pl: 2.5, my: 0.5 },
        ...sx,
      }}
    >
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 &&
            (inline ? (
              <br />
            ) : (
              <Box component="span" sx={{ display: 'block', height: 6 }} />
            ))}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={inline ? { p: ({ children }: { children?: React.ReactNode }) => <>{children}</> } : undefined}
          >
            {line}
          </ReactMarkdown>
        </React.Fragment>
      ))}
    </Box>
  );
}

// Keep the preview faithful to the DOCX renderer
// (backend/services/document_renderer.py): coerce stray objects to text, scrub
// internal staging identifiers, and render the "How this was calculated" note.
const FRAME_ID_RE = /\bt_frame_[0-9a-fA-F]+\b/g;
const FRAME_PAREN_RE = /\s*\((?:sources?)?[:\s]*[^)]*t_frame_[0-9a-fA-F]+[^)]*\)/gi;

function coerceText(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(coerceText).join(' ');
  if (typeof value === 'object') {
    for (const key of ['content', 'text', 'value', 'label', 'name']) {
      const v = value[key];
      if (typeof v === 'string' && v.trim()) return v;
    }
    return Object.values(value)
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .join(' ');
  }
  return String(value);
}

function scrubInternal(value: any): string {
  let s = coerceText(value);
  if (!s) return '';
  s = s.replace(FRAME_PAREN_RE, '');
  s = s.replace(FRAME_ID_RE, '');
  s = s.replace(/source[:\s]*frames?\b[:\s]*/gi, '');
  s = s.replace(/\s{2,}/g, ' ');
  s = s.replace(/\s+([.,;:])/g, '$1');
  return s.replace(/^[\s,;:\u2014-]+|[\s,;:\u2014-]+$/g, '');
}

/** Mirror document_renderer._insert_label_line_breaks — put structural labels on their own line. */
function insertLabelLineBreaks(text: string): string {
  if (!text) return text;
  return text.replace(
    /(?<=\S)\s+((?:\*\*)?(?:Finding|Suggestion|So what)\s*:(?:\*\*)?)/gi,
    '\n$1',
  );
}

function MethodNote({ text }: { text?: any }) {
  const body = scrubInternal(text);
  if (!body) return null;
  return (
    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontWeight: 700, color: '#2F6FED' }}>
        How this was calculated:{' '}
      </Box>
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {body}
      </Box>
    </Typography>
  );
}

const CALLOUT_STYLES: Record<
  string,
  { bg: string; bar: string; label: string; fg: string }
> = {
  insight: { bg: '#EEF4FF', bar: '#2F6FED', label: 'INSIGHT', fg: '#1B3A8A' },
  success: { bg: '#ECFDF3', bar: '#1B873F', label: "WHAT'S WORKING", fg: '#0F5C2E' },
  warning: { bg: '#FEF6E7', bar: '#B7791F', label: 'WATCH', fg: '#8A5A00' },
  risk: { bg: '#FEF3F2', bar: '#C0392B', label: 'RISK', fg: '#9B2C20' },
};

function deltaColor(sentiment: DocSpecMetricItem['sentiment'], theme: any): string {
  if (sentiment === 'positive') return theme.palette.success.main;
  if (sentiment === 'negative') return theme.palette.error.main;
  return theme.palette.text.secondary;
}

function directionGlyph(direction: DocSpecMetricItem['direction']): string {
  if (direction === 'up') return '▲';
  if (direction === 'down') return '▼';
  if (direction === 'flat') return '▬';
  return '';
}

function MetricCard({ item }: { item: DocSpecMetricItem }) {
  const theme = useTheme();
  const glyph = directionGlyph(item.direction);
  return (
    <Box
      sx={{
        flex: '1 1 140px',
        minWidth: 140,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.default,
      }}
    >
      {item.label && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}
        >
          {item.label}
        </Typography>
      )}
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
        {item.value ?? '—'}
      </Typography>
      {(item.delta || glyph) && (
        <Typography variant="caption" sx={{ color: deltaColor(item.sentiment, theme), fontWeight: 600 }}>
          {glyph} {item.delta}
        </Typography>
      )}
      {item.context && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          {item.context}
        </Typography>
      )}
    </Box>
  );
}

function SectionView({ section }: { section: DocSpecSection }) {
  const theme = useTheme();
  const s = section as Record<string, any>;

  switch (section.type) {
    case 'heading': {
      const level = (s.level as number) || 1;
      const variant = level === 1 ? 'h6' : level === 2 ? 'subtitle1' : 'subtitle2';
      return (
        <Typography
          variant={variant as any}
          sx={{
            fontWeight: 700,
            mt: level === 1 ? 2.5 : 1.5,
            mb: 0.5,
            ...(level === 1 && {
              pb: 0.5,
              borderBottom: `2px solid ${theme.palette.divider}`,
            }),
          }}
        >
          {scrubInternal(s.content)}
        </Typography>
      );
    }

    case 'text':
      return <Markdown text={scrubInternal(s.content)} sx={{ color: 'text.primary', fontSize: 14, lineHeight: 1.6 }} />;

    case 'callout': {
      const variant = (s.variant as string) || 'insight';
      const cs = CALLOUT_STYLES[variant] || CALLOUT_STYLES.insight;
      return (
        <Box
          sx={{
            my: 1.5,
            p: 1.5,
            pl: 2,
            borderRadius: 1,
            bgcolor: cs.bg,
            borderLeft: `4px solid ${cs.bar}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: cs.bar, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' }}
          >
            {s.title || cs.label}
          </Typography>
          {s.content && (
            <Markdown text={scrubInternal(s.content)} sx={{ color: cs.fg, mt: 0.5, fontSize: 14, lineHeight: 1.55 }} />
          )}
        </Box>
      );
    }

    case 'metrics': {
      const items = (s.items as DocSpecMetricItem[]) || [];
      return (
        <Box sx={{ my: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {items.map((it, i) => (
              <MetricCard key={i} item={it} />
            ))}
          </Box>
          <MethodNote text={s.method} />
        </Box>
      );
    }

    case 'metric':
      return (
        <Box sx={{ my: 1.5, maxWidth: 220 }}>
          <MetricCard item={s as DocSpecMetricItem} />
          <MethodNote text={s.method} />
        </Box>
      );

    case 'table': {
      const columns = (s.columns as string[]) || [];
      const rows = (s.rows as Array<Array<string | number | null>>) || [];
      const align = (s.align as Array<'left' | 'right' | 'center'>) || [];
      const totalRow = Boolean(s.total_row);
      return (
        <Box sx={{ my: 1.5 }}>
          {s.title && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {s.title}
            </Typography>
          )}
          {scrubInternal(s.caption) && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              {scrubInternal(s.caption)}
            </Typography>
          )}
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflowX: 'auto', overflowY: 'hidden' }}>
            <Table
              size="small"
              sx={{
                minWidth: columns.length > 3 ? Math.min(columns.length * 120, 1100) : 'auto',
                '& td, & th': { fontSize: 13, whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                  {columns.map((c, i) => (
                    <TableCell key={i} align={align[i] || 'left'} sx={{ fontWeight: 700 }}>
                      {c}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, ri) => {
                  const isTotal = totalRow && ri === rows.length - 1;
                  return (
                    <TableRow
                      key={ri}
                      sx={{
                        bgcolor: ri % 2 === 1 ? theme.palette.action.hover : 'transparent',
                        ...(isTotal && { '& td': { fontWeight: 700, borderTop: `2px solid ${theme.palette.divider}` } }),
                      }}
                    >
                      {row.map((cell, ci) => (
                        <TableCell key={ci} align={align[ci] || 'left'}>
                          {cell === null || cell === undefined ? '' : scrubInternal(cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          {/* Internal data-source attributions (frame IDs) are intentionally
              not rendered — they are plumbing, not user-facing. */}
          <MethodNote text={s.method} />
        </Box>
      );
    }

    case 'list': {
      const items = (s.items as any[]) || [];
      const ordered = Boolean(s.ordered);
      const ListTag = ordered ? 'ol' : 'ul';
      return (
        <Box component={ListTag} sx={{ pl: 3, my: 1, '& li': { mb: 0.5, fontSize: 14, lineHeight: 1.55 } }}>
          {items
            .map((it) => scrubInternal(it))
            .filter((t) => t.trim())
            .map((t, i) => (
              <li key={i}>
                <Markdown inline text={t} />
              </li>
            ))}
        </Box>
      );
    }

    case 'quote':
      return (
        <Box
          sx={{
            my: 1.5,
            pl: 2,
            borderLeft: `3px solid ${theme.palette.divider}`,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          <Typography variant="body2" component="div" sx={{ fontStyle: 'italic' }}>
            “<Markdown inline text={scrubInternal(s.content)} />”
          </Typography>
          {s.attribution && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              — {s.attribution}
            </Typography>
          )}
        </Box>
      );

    case 'method':
    case 'methodology':
      return <MethodNote text={s.content} />;

    case 'source': {
      const src = scrubInternal(s.content);
      return src ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', my: 0.5, fontStyle: 'italic' }}>
          {src}
        </Typography>
      ) : null;
    }

    case 'divider':
      return <Divider sx={{ my: 1.5 }} />;

    case 'page_break':
      return (
        <Box sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Divider sx={{ flex: 1, borderStyle: 'dashed' }} />
          <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Page break
          </Typography>
          <Divider sx={{ flex: 1, borderStyle: 'dashed' }} />
        </Box>
      );

    default: {
      const content = scrubInternal(s.content);
      return content ? (
        <Typography variant="body2" sx={{ my: 0.75 }}>
          {content}
        </Typography>
      ) : null;
    }
  }
}

export default function DocSpecPreview({ spec, maxHeight = 460 }: DocSpecPreviewProps) {
  const theme = useTheme();
  const sections = spec.sections || [];
  const metaParts = [spec.period, spec.author, spec.confidentiality].filter(Boolean) as string[];

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: '#fff',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Accent bar to evoke the themed cover */}
      <Box sx={{ height: 4, bgcolor: '#2F6FED' }} />
      <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3, color: '#2B2F36' }}>
        {/* Cover */}
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1F2A44', lineHeight: 1.2 }}>
          {spec.title || 'Untitled report'}
        </Typography>
        {spec.subtitle && (
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {spec.subtitle}
          </Typography>
        )}
        {metaParts.length > 0 && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 1, letterSpacing: 0.3 }}
          >
            {metaParts.join('  ·  ')}
          </Typography>
        )}
        <Divider sx={{ mt: 2, mb: 1 }} />

        {sections.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
            This report has no content sections.
          </Typography>
        ) : (
          sections.map((section, i) => <SectionView key={i} section={section} />)
        )}
      </Box>
    </Box>
  );
}
